import { useState, type FormEvent } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useAreas, useCreateNote, useNotes } from '../lib/hooks';
import { useT } from '../lib/i18n';
import { Modal } from '../components/modal';

export function NotesPage() {
  const t = useT();
  const notes = useNotes();
  const areas = useAreas();
  const create = useCreateNote();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [areaId, setAreaId] = useState('');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    await create.mutateAsync({
      title: title.trim() || null,
      content: content.trim(),
      areaId: areaId || null,
    });
    setTitle('');
    setContent('');
    setAreaId('');
    setOpen(false);
    toast.success('Đã tạo note');
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 lg:p-8">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {t('notes.title')}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{notes.data?.length ?? 0} ghi chú</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" />
          {t('notes.add')}
        </button>
      </header>

      {notes.isLoading ? (
        <p className="text-sm text-slate-500">{t('common.loading')}</p>
      ) : notes.data && notes.data.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {notes.data.map((n) => (
            <article key={n.id} className="panel">
              <div className="panel-body space-y-2">
                {n.title && (
                  <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    {n.title}
                  </h3>
                )}
                <p className="text-sm whitespace-pre-wrap text-slate-700 dark:text-slate-300">
                  {n.content}
                </p>
                <div className="flex items-center justify-between pt-2 text-[11px] text-slate-500">
                  <span>{new Date(n.createdAt).toLocaleString('vi')}</span>
                  {n.areaId && (
                    <span className="badge-info">
                      {areas.data?.find((a) => a.id === n.areaId)?.name ?? '—'}
                    </span>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="panel">
          <div className="panel-body py-12 text-center text-sm text-slate-500">
            {t('notes.empty')}
          </div>
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('notes.add')}
        footer={
          <>
            <button type="button" onClick={() => setOpen(false)} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              form="note-form"
              className="btn-primary"
              disabled={create.isPending}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <form id="note-form" onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label">Tiêu đề (tuỳ chọn)</label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <label className="label">Nội dung</label>
            <textarea
              className="input min-h-[180px] resize-y"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="label">Area</label>
            <select className="input" value={areaId} onChange={(e) => setAreaId(e.target.value)}>
              <option value="" className="bg-slate-900">
                — Không gắn —
              </option>
              {areas.data?.map((a) => (
                <option key={a.id} value={a.id} className="bg-slate-900">
                  {a.name}
                </option>
              ))}
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}
